import extend from 'extend';
import ScriptLoader from '../ScriptLoader/ScriptLoader';
import configHelper from '../Config/Config';
import adobeLanuchSnippet from './AdobeLaunchSnippet';
// import Device from '../Device/Device';

/**
 * Private function - used with in a class - contains ensighten lib related functions to tag JCP page events
 * @param eventName - Event name
 */
function triggerEvent(eventName, digitalData) {
    if (window.Bootstrapper || window._satellite) {
        console.log(`ensighten event name: ${eventName}`); // @TODO- PR: ingore this log for now
        /* istanbul ignore else */
        if (window.pageDataTracker && window.pageDataTracker.trackAnalyticsEvent) {
            console.log(
                `window.pageDataTracker.trackAnalyticsEvent available: event name: ${eventName}`
            ); // @TODO- PR: ingore this log for now
            window.pageDataTracker.trackAnalyticsEvent(eventName, digitalData);
        }
    }
}

/**
 * Helper class to track ensighten tag events
 * Helper class will expose below two attribute in a Object(analyticsAPI) to use in page level Components to track any analytics events
 * analyticsConstants {object} - contains unique keys for each page level analytics tagging names
 * track {function} - all page level components will use this function to tag any event related to analytics
 *
 */
class Analytics {
    /**
     * With new instant, it will call init() function and download ensighten related js files
     * though constructor we are exposing only one helper Object to consume ensighten services
     */
    constructor() {
        this.eventQueue = [];
        this.hasInitialised = false;
        this.analyticsPageViewTriggered = false;
        this._adobeLaunchLoadedSuccess = this._adobeLaunchLoadedSuccess.bind(this);
        /* istanbul ignore if */
        if (!__SERVER__ && !window.eventQueue && this.isEnableAnalyticsPublicQueue()) {
            window.eventQueue = [];
        }
    }

    isEnableAnalyticsPublicQueue = () =>
        !__SERVER__ &&
        window.__PRELOADED_STATE__ &&
        window.__PRELOADED_STATE__.context.featureFlags.enableAnalyticsPublicQueue;

    /**
     * Initialize analytics Helper component
     * Here it will start download ensighten related js files and appended to document object to available for global window level
     * Once js files are ready to server, on callback success it will trigger all pending events
     */
    init(config) {
        /*
         * This event will execute after digitalDataReady event successfully fired.
         */
        /* istanbul ignore next */
        document.addEventListener('analyticsPageViewTriggered', () => {
            this.analyticsPageViewTriggered = true;
        });
        /*
         * This event will execute after all ensighten lib files are downloaded.
         */
        /* istanbul ignore next */
        document.addEventListener('analyticsFunctionsLoaded', () => {
            this.submitDigitalDataReadyEvent();
        });
        // const deviceType = Device.findDeviceType();
        /* istanbul ignore next */
        if (window.location.search.indexOf('debug=disableEnsighten') === -1) {
            /* istanbul ignore next */
            window.addEventListener('load', () => {
                /* istanbul ignore next */
                if (config) {
                    const jqueryLibProps = {
                        src: config.jqueryUrl,
                    };
                    ScriptLoader.load(jqueryLibProps);
                }
            });
        }
    }

    /* istanbul ignore next */
    initLaunch(config) {
        /*
         * This event will execute after digitalDataReady event successfully fired.
         */
        /* istanbul ignore next */
        document.addEventListener('analyticsPageViewTriggered', () => {
            this.analyticsPageViewTriggered = true;
            this._adobeLaunchLoadedSuccess();
        });
        /*
         * This event will execute after all adobeLaunch lib files are downloaded.
         */
        /* istanbul ignore next */
        document.addEventListener('analyticsFunctionsLoaded', () => {
            this.submitDigitalDataReadyEvent();
        });
        // const deviceType = Device.findDeviceType();
        /* istanbul ignore next */
        if (window.location.search.indexOf('debug=disableTagManagement') === -1) {
            /* istanbul ignore next */
            window.addEventListener('load', () => {
                /* istanbul ignore next */
                if (config) {
                    const isBot = configHelper('context.deviceType.isBot', false);
                    if (!isBot) {
                        const adobeLaunchLibProps = {
                            src: config.adobeLaunchUrl,
                            onSuccess: this._adobeLaunchLoadedSuccess.bind(this),
                        };
                        ScriptLoader.loadSnippet(adobeLanuchSnippet, adobeLaunchLibProps);
                    }
                }
            });
        }
    }

    /* istanbul ignore next */
    _adobeLaunchLoadedSuccess() {
        if (!(window || {}).analyticsPageViewTagTriggered || this.analyticsPageViewTriggered) {
            if (!this.hasInitialised) {
                this.hasInitialised = true;
                this.submitPendingEvents();
            }
        }
    }

    /**
     * Public function - This will be expose to all page level components to tag event by name
     * @param eventName {string} - event type
     * @param digitalData - {object} contains each page level component data required by ensighten tagging
     * @param reset - {boolean} flag to turn off the extend mode, defaults mode is true - even if not sent, send false to turn off
     */
    track(eventName, digitalData, reset) {
        let data = null;
        if (eventName === 'digitalDataReady') {
            window.digitalData = extend(!!reset, window.digitalData || {}, digitalData || {});
        } else if (eventName === 'inventoryProductOOS') {
            /* This digitalDataInventory object provides the count of in-stock or out-of-stock items for specific lots. */
            window.digitalDataInventory = extend(
                !!reset,
                window.digitalDataInventory || {},
                digitalData || {}
            );
        } else {
            data = digitalData || null;
        }

        if (eventName) {
            /* istanbul ignore if */
            if (this.isEnableAnalyticsPublicQueue()) {
                /* istanbul ignore if */
                console.log('Analytics public queue enabled.');
                if (window.pageDataTracker && window.pageDataTracker.trackAnalyticsEvent) {
                    console.log('Analytics public queue enabled. trackAnalyticsEvent present');
                    triggerEvent(eventName, data);
                } else {
                    if (!window.eventQueue) {
                        window.eventQueue = [];
                    }
                    console.log('Analytics public queue enabled. trackAnalyticsEvent not present');
                    const event = { eventName, eventParam: data };
                    console.log('Analytics public queue enabled. event pushed to queue: ', event);
                    window.eventQueue.push(event);
                }
            } else if (this.hasInitialised) {
                console.log('Analytics public queue not enabled.');
                triggerEvent(eventName, data);
            } else {
                this.eventQueue.push({ eventName, digitalData: data });
            }
        }
    }

    /**
     * This flushes the pending digitalDataReady event to ensighten
     */

    submitDigitalDataReadyEvent() {
        const pendingEvents = [];
        this.eventQueue.forEach((eventDetails) => {
            if (eventDetails.eventName === 'digitalDataReady') {
                triggerEvent(eventDetails.eventName, eventDetails.digitalData);
            } else if (eventDetails.eventName === 'inventoryProductOOS') {
                triggerEvent(eventDetails.eventName, eventDetails.digitalData);
            } else {
                pendingEvents.push(eventDetails);
            }
        });
        this.eventQueue = pendingEvents;
    }

    /**
     * This flushes the pending client side events to ensighten
     */
    submitPendingEvents() {
        this.eventQueue.forEach((eventDetails) => {
            triggerEvent(eventDetails.eventName, eventDetails.digitalData);
        });

        this.eventQueue = [];
    }
}

const analytics = new Analytics();
export default analytics;
